// components/screener/FilterCard.jsx
import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box, Paper, Typography, IconButton, TextField, Stack, Button,
    Menu, MenuItem, Divider, Select, FormControl, InputAdornment,
    Popover, Checkbox
} from '@mui/material';
import {
    Close as CloseIcon,
    KeyboardArrowDown as ChevronIcon,
    RestartAlt as ResetIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import { formatPresetLabel, formatBetweenLabel } from './screenerConfig';

const FilterCard = ({ filter, onRemove, onUpdate, filterOptions = {} }) => {
    const theme = useTheme();
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuMode, setMenuMode] = useState(null);
    // MultiSelect popover state
    const [multiSelectAnchor, setMultiSelectAnchor] = useState(null);
    const [multiSelectSearch, setMultiSelectSearch] = useState('');

    const openMenu = (event, mode) => {
        setMenuAnchor(event.currentTarget);
        setMenuMode(mode);
    };

    const closeMenu = () => {
        setMenuAnchor(null);
        setMenuMode(null);
    };

    const selectPreset = (mode, value) => {
        if (mode === 'above') {
            onUpdate(filter.id, { min: value, max: '', mode: 'above' });
        } else if (mode === 'below') {
            onUpdate(filter.id, { min: '', max: value, mode: 'below' });
        } else if (mode === 'between') {
            onUpdate(filter.id, { min: value[0], max: value[1], mode: 'between' });
        }
        closeMenu();
    };

    const setCustomMode = () => {
        onUpdate(filter.id, { min: '', max: '', mode: 'custom' });
        closeMenu();
    };

    // ── Summary text for current selection ───────────────────────────────
    const getSummary = () => {
        if (!filter.values || filter.values.mode === 'custom') return null;
        const { min, max, mode } = filter.values;
        const fmt = filter.format || 'number';

        if (mode === 'above' && min !== '') return formatPresetLabel(min, fmt, 'above');
        if (mode === 'below' && max !== '') return formatPresetLabel(max, fmt, 'below');
        if (mode === 'between' && min !== '' && max !== '') return formatBetweenLabel(min, max, fmt);
        return null;
    };

    // ── Render by filter type ────────────────────────────────────────────

    const renderRange = () => {
        const mode = filter.values?.mode || 'custom';
        const presets = filter.presets || {};
        const summary = getSummary();

        return (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
                {/* Mode buttons */}
                <Stack direction="row" spacing={0.5}>
                    {['above', 'below', 'between'].map((m) => (
                        <Button
                            key={m}
                            size="small"
                            variant={mode === m ? 'contained' : 'outlined'}
                            onClick={(e) => openMenu(e, m)}
                            endIcon={<ChevronIcon sx={{ fontSize: '16px !important' }} />}
                            sx={{
                                flex: 1,
                                fontSize: '0.7rem',
                                py: 0.5,
                                px: 0.5,
                                minWidth: 0,
                                textTransform: 'capitalize',
                            }}
                        >
                            {m === 'above' ? 'Over' : m === 'below' ? 'Under' : 'Between'}
                        </Button>
                    ))}
                </Stack>

                {/* Preset menu */}
                <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={closeMenu}
                    PaperProps={{
                        sx: {
                            mt: 0.5,
                            width: 200,
                            maxHeight: 300,
                            bgcolor: theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                        },
                    }}
                >
                    {menuMode && presets[menuMode]?.map((val, i) => {
                        const isArray = Array.isArray(val);
                        const label = isArray
                            ? formatBetweenLabel(val[0], val[1], filter.format || 'number')
                            : formatPresetLabel(val, filter.format || 'number', menuMode);
                        return (
                            <MenuItem
                                key={i}
                                onClick={() => selectPreset(menuMode, val)}
                                sx={{ fontSize: '0.85rem', color: theme.palette.text.primary }}
                            >
                                {label}
                            </MenuItem>
                        );
                    })}
                    <Divider />
                    <MenuItem onClick={setCustomMode} sx={{ fontSize: '0.85rem', color: theme.palette.text.secondary }}>
                        Custom value
                    </MenuItem>
                </Menu>

                {/* Custom inputs or summary */}
                {mode === 'custom' ? (
                    <Stack direction="row" spacing={0.5}>
                        <TextField
                            size="small"
                            placeholder="Min"
                            value={filter.values?.min ?? ''}
                            onChange={(e) => onUpdate(filter.id, { ...filter.values, min: e.target.value })}
                            sx={compactInputSx(theme)}
                        />
                        <TextField
                            size="small"
                            placeholder="Max"
                            value={filter.values?.max ?? ''}
                            onChange={(e) => onUpdate(filter.id, { ...filter.values, max: e.target.value })}
                            sx={compactInputSx(theme)}
                        />
                    </Stack>
                ) : summary ? (
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 1.5, py: 0.75,
                        borderRadius: 1,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,127,255,0.08)' : 'rgba(0,127,255,0.05)',
                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(0,127,255,0.2)' : 'rgba(0,127,255,0.15)'}`,
                    }}>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', color: theme.palette.primary.main, fontWeight: 600 }}>
                            {summary}
                        </Typography>
                        <IconButton size="small" onClick={() => onUpdate(filter.id, { min: '', max: '', mode: 'custom' })} sx={{ p: 0.25 }}>
                            <ResetIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                        </IconButton>
                    </Box>
                ) : null}
            </Stack>
        );
    };

    const renderSelect = () => {
        const options = filter.options || filterOptions[filter.optionsKey] || [];
        return (
            <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                <Select
                    value={filter.values || ''}
                    onChange={(e) => onUpdate(filter.id, e.target.value)}
                    displayEmpty
                    MenuProps={{ PaperProps: { sx: { maxHeight: 250, bgcolor: theme.palette.background.paper } } }}
                    sx={selectSx(theme)}
                >
                    <MenuItem value="" sx={{ opacity: 0.6, fontSize: '0.85rem' }}>Select {filter.label}</MenuItem>
                    {options.map((opt) => (
                        <MenuItem key={opt} value={opt} sx={{ fontSize: '0.85rem' }}>{opt}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };

    const renderMultiSelect = () => {
        const options = filter.options || filterOptions[filter.optionsKey] || [];
        const selected = filter.values || [];
        const open = Boolean(multiSelectAnchor);

        const filtered = options.filter(opt =>
            opt.toLowerCase().includes(multiSelectSearch.toLowerCase())
        );

        const handleToggle = (opt) => {
            const newValues = selected.includes(opt)
                ? selected.filter(v => v !== opt)
                : [...selected, opt];
            onUpdate(filter.id, newValues);
        };

        return (
            <Box sx={{ mt: 1.5 }}>
                {/* Trigger button */}
                <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    onClick={(e) => setMultiSelectAnchor(e.currentTarget)}
                    endIcon={<ChevronIcon sx={{ fontSize: '16px !important' }} />}
                    sx={{
                        justifyContent: 'space-between',
                        textTransform: 'none',
                        fontSize: '0.82rem',
                        color: selected.length > 0 ? theme.palette.text.primary : theme.palette.text.secondary,
                        borderColor: theme.palette.divider,
                        py: 0.75,
                        '&:hover': { borderColor: theme.palette.primary.main },
                    }}
                >
                    {selected.length > 0 ? selected.join(', ') : `Select ${filter.label}`}
                </Button>

                {/* Popover dropdown with search + checkboxes */}
                <Popover
                    open={open}
                    anchorEl={multiSelectAnchor}
                    onClose={() => { setMultiSelectAnchor(null); setMultiSelectSearch(''); }}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    PaperProps={{
                        sx: {
                            mt: 0.5,
                            borderRadius: 2,
                            width: multiSelectAnchor?.offsetWidth || 220,
                            maxHeight: 320,
                            display: 'flex',
                            flexDirection: 'column',
                            bgcolor: theme.palette.mode === 'dark'
                                ? 'rgba(10, 25, 41, 0.97)' : theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                        },
                    }}
                >
                    {/* Search input */}
                    <Box sx={{ p: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
                        <TextField
                            size="small"
                            fullWidth
                            autoFocus
                            placeholder={`Search ${filter.label.toLowerCase()}...`}
                            value={multiSelectSearch}
                            onChange={(e) => setMultiSelectSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ fontSize: '1rem', color: theme.palette.text.secondary }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    fontSize: '0.8rem',
                                    height: 32,
                                    color: theme.palette.text.primary,
                                    '& fieldset': { borderColor: theme.palette.divider },
                                },
                            }}
                        />
                    </Box>

                    {/* Options list */}
                    <Box sx={{ overflow: 'auto', flex: 1 }}>
                        {filtered.length === 0 ? (
                            <Typography variant="caption" color="text.secondary"
                                sx={{ p: 1.5, display: 'block', textAlign: 'center' }}>
                                No matches
                            </Typography>
                        ) : (
                            filtered.map((opt) => (
                                <MenuItem
                                    key={opt}
                                    onClick={() => handleToggle(opt)}
                                    dense
                                    sx={{
                                        py: 0.25, px: 1, minHeight: 32,
                                        bgcolor: selected.includes(opt)
                                            ? (theme.palette.mode === 'dark' ? 'rgba(0,127,255,0.08)' : 'rgba(0,127,255,0.04)')
                                            : 'transparent',
                                    }}
                                >
                                    <Checkbox
                                        checked={selected.includes(opt)}
                                        size="small"
                                        sx={{ p: 0.25, mr: 0.75 }}
                                    />
                                    <Typography variant="body2" sx={{ fontSize: '0.82rem' }}>
                                        {opt}
                                    </Typography>
                                </MenuItem>
                            ))
                        )}
                    </Box>

                    {/* Clear all */}
                    {selected.length > 0 && (
                        <Box sx={{
                            p: 0.75,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            display: 'flex',
                            justifyContent: 'flex-end',
                        }}>
                            <Button
                                size="small"
                                onClick={() => onUpdate(filter.id, [])}
                                sx={{ textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
                            >
                                Clear all
                            </Button>
                        </Box>
                    )}
                </Popover>
            </Box>
        );
    };

    const renderBoolean = () => (
        <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }}>
            <Button
                size="small"
                variant={filter.values === true ? 'contained' : 'outlined'}
                onClick={() => onUpdate(filter.id, true)}
                sx={{ flex: 1, fontSize: '0.8rem' }}
            >
                Yes
            </Button>
            <Button
                size="small"
                variant={filter.values === false ? 'contained' : 'outlined'}
                onClick={() => onUpdate(filter.id, false)}
                sx={{ flex: 1, fontSize: '0.8rem' }}
            >
                No
            </Button>
        </Stack>
    );

    const renderComposite = () => {
        const vals = filter.values || {};
        return (
            <Stack spacing={1} sx={{ mt: 1.5 }}>
                <FormControl fullWidth size="small">
                    <Select
                        value={vals.averageType || '50'}
                        onChange={(e) => onUpdate(filter.id, { ...vals, averageType: e.target.value })}
                        sx={selectSx(theme)}
                    >
                        {['20', '50', '100', '150', '200'].map(v => (
                            <MenuItem key={v} value={v} sx={{ fontSize: '0.85rem' }}>{v}-Day SMA</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                    <Select
                        value={vals.comparisonType || 'range'}
                        onChange={(e) => onUpdate(filter.id, {
                            ...vals,
                            comparisonType: e.target.value,
                            minPercentage: '0',
                            maxPercentage: e.target.value === 'range' ? '5' : undefined,
                        })}
                        sx={selectSx(theme)}
                    >
                        <MenuItem value="range" sx={{ fontSize: '0.85rem' }}>Within Range</MenuItem>
                        <MenuItem value="above" sx={{ fontSize: '0.85rem' }}>Above</MenuItem>
                        <MenuItem value="below" sx={{ fontSize: '0.85rem' }}>Below</MenuItem>
                    </Select>
                </FormControl>
                {vals.comparisonType === 'range' ? (
                    <Stack direction="row" spacing={0.5}>
                        <TextField
                            size="small" placeholder="Min %"
                            value={vals.minPercentage || '0'}
                            onChange={(e) => onUpdate(filter.id, { ...vals, minPercentage: e.target.value })}
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            sx={compactInputSx(theme)}
                        />
                        <TextField
                            size="small" placeholder="Max %"
                            value={vals.maxPercentage || '5'}
                            onChange={(e) => onUpdate(filter.id, { ...vals, maxPercentage: e.target.value })}
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            sx={compactInputSx(theme)}
                        />
                    </Stack>
                ) : (
                    <TextField
                        size="small" placeholder="Percentage"
                        value={vals.minPercentage || '0'}
                        onChange={(e) => onUpdate(filter.id, { ...vals, minPercentage: e.target.value })}
                        InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                        sx={compactInputSx(theme)}
                    />
                )}
            </Stack>
        );
    };

    const renderContent = () => {
        switch (filter.type) {
            case 'range': return renderRange();
            case 'select': return renderSelect();
            case 'multiSelect': return renderMultiSelect();
            case 'boolean': return renderBoolean();
            case 'composite': return renderComposite();
            default: return null;
        }
    };

    return (
        <Paper
            variant="stats"
            sx={{
                p: 1.5,
                borderRadius: 2,
                position: 'relative',
                transition: 'box-shadow 0.2s',
                '&:hover': {
                    boxShadow: theme.palette.mode === 'dark'
                        ? '0 4px 20px rgba(0,0,0,0.3)'
                        : '0 4px 20px rgba(0,0,0,0.08)',
                },
            }}
        >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography
                    variant="subtitle2"
                    sx={{
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                        letterSpacing: '0.02em',
                        textTransform: 'uppercase',
                    }}
                >
                    {filter.label}
                </Typography>
                <IconButton
                    size="small"
                    onClick={() => onRemove(filter.id)}
                    sx={{
                        p: 0.25,
                        color: theme.palette.text.secondary,
                        '&:hover': { color: theme.palette.error.main },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Stack>
            {renderContent()}
        </Paper>
    );
};

// ── Shared styles ────────────────────────────────────────────────────────────

const compactInputSx = (theme) => ({
    '& .MuiOutlinedInput-root': {
        color: theme.palette.text.primary,
        fontSize: '0.85rem',
        '& fieldset': {
            borderColor: theme.palette.divider,
        },
        '&:hover fieldset': {
            borderColor: theme.palette.primary.main,
        },
    },
    '& .MuiInputBase-input': {
        py: 0.75,
        px: 1,
    },
    '& .MuiInputBase-input::placeholder': {
        color: theme.palette.text.secondary,
        fontSize: '0.8rem',
    },
});

const selectSx = (theme) => ({
    fontSize: '0.85rem',
    color: theme.palette.text.primary,
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
    },
    '& .MuiSvgIcon-root': {
        color: theme.palette.text.secondary,
    },
});

export default FilterCard;